import { useRef, useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppCTA from "@/components/WhatsAppCTA";
import Home from "@/pages/Home";
import FeasibilityTool from "@/pages/FeasibilityTool";
import CostCalculator from "@/pages/CostCalculator";
import BriefBuilder from "@/pages/BriefBuilder";
import Login from "@/pages/Login";
import AdminDashboard from "@/pages/AdminDashboard";
import ProjectFitScore from "@/pages/ProjectFitScore";
import AgenticArchitect from "@/pages/AgenticArchitect";
import Projects from "@/pages/Projects";
import About from "@/pages/About";
import AskArchitect from "@/pages/AskArchitect";
import api from "@/lib/api";

function AuthCallback() {
  const hasProcessed = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get('session_id');

    if (!sessionId) {
      navigate('/login', { replace: true });
      return;
    }

    // Exchange session_id with backend
    api.post('/auth/google/callback', { session_id: sessionId })
      .then(({ data }) => {
        // Clear hash from URL
        window.history.replaceState(null, '', window.location.pathname);
        setUser(data);
        // Redirect based on role
        if (data.role === 'admin') {
          navigate('/admin', { replace: true, state: { user: data } });
        } else {
          navigate('/', { replace: true, state: { user: data } });
        }
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || 'Authentication failed';
        window.history.replaceState(null, '', '/login');
        navigate('/login', { replace: true, state: { error: msg } });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-light" data-testid="auth-callback">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-brand-dark/60 font-body text-sm">Authenticating with Google...</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-brand-light">
      <div className="w-8 h-8 border-2 border-brand-accent border-t-transparent rounded-full animate-spin" />
    </div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();

  // CRITICAL: Detect session_id in hash synchronously during render (prevents race conditions)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/feasibility" element={<FeasibilityTool />} />
          <Route path="/cost-calculator" element={<CostCalculator />} />
          <Route path="/brief-builder" element={<BriefBuilder />} />
          <Route path="/fit-score" element={<ProjectFitScore />} />
          <Route path="/architect" element={<AgenticArchitect />} />
          <Route path="/projects" element={<Projects />} />
          <Route path="/about" element={<About />} />
          <Route path="/ask-architect" element={<AskArchitect />} />
          <Route path="/login" element={<Login />} />
          <Route path="/admin/*" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
        </Routes>
      </main>
      <Footer />
      <WhatsAppCTA />
      <Toaster position="top-right" richColors />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
